import * as React from "react";
import { graphql } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Media } from "../components/Media";
import Seo from "../components/Seo";
import AussieOwned from "../components/seals/AustralianOwned";
import Vegan from "../components/seals/VeganFriendly";
import GlutenFree from "../components/seals/GlutenFree";
import SugarFree from "../components/seals/SugarFree";
import StickyWrapper from "../components/StickyWrapper";
import CustomerQuote from "../components/CustomerQuote";
import ogImage from "../images/jointPainSucks/joint-pain-sucks-rugby.jpg";
import "../styles/jointPainSucks.css";

const JointPainSucks = ({ data }) => {
  const product = getImage(data.product);
  const products = getImage(data.products);
  const boxer = getImage(data.boxer);
  const boswellia = getImage(data.boswellia);
  const turmeric = getImage(data.turmeric);
  const ginger = getImage(data.ginger);
  const pepper = getImage(data.pepper);

  return (
    <>
      <Seo
        title="Joint Pain Sucks™: Triple-Action Joint Support"
        description="A combination of Boswellia, Turmeric, Ginger and Black Pepper to reduce and relieve joint pain and inflammation. Made in Australia."
        image={`https://www.ithrivenutrition.com.au${ogImage}`}
        alt="Joint Pain Sucks bottle in front of a rugby game."
        handle="joint-pain-sucks"
      />
      <div className="bg-orange">
        <div className="max-w-screen-lg mx-auto">
          <p className="text-white py-2 text-center">
            <strong>Joint Pain Sucks™</strong> is currently unavailable and is
            being reformulated to make it better than ever.
          </p>
        </div>
      </div>
      <div className="bg-white">
        <div className="bg-gray-darker">
          <div className="max-w-screen-lg m-auto pt-2 md:pt-6 lg:pt-10">
            <StickyWrapper
              title="Joint Pain Sucks"
              subtitle="Potent Triple-Action Formula For Joint Pain And Inflammation"
              shopLink="joint-pain-sucks"
            />
            <div className="flex flex-wrap-reverse md:flex-nowrap">
              <div className="text-white mt-0 md:mt-10 w-full px-6 md:p-0 md:w-5/12">
                <p>
                  <strong>Joint Pain Sucks™</strong> reduces and relieves the
                  symptoms associated with:
                </p>
                <ul className="list-inside mt-2">
                  <li className="list-circle white">Joint Pain</li>
                  <li className="list-circle white">Joint inflammation</li>
                  <li className="list-circle white">Joint stiffness</li>
                  <li className="list-circle white">
                    Mild arthritis and osteoartritis
                  </li>
                </ul>

                <div className="flex justify-center my-10">
                  <AussieOwned fill="#FF5E0A" />
                  <Vegan fill="#FF5E0A" />
                  <SugarFree fill="#FF5E0A" />
                  <GlutenFree fill="#FF5E0A" />
                </div>
              </div>
              <div className="w-full text-right -mb-8 md:mb-0 md:w-7/12 relative">
                <Media lessThan="md">
                  <GatsbyImage
                    image={product}
                    alt="You Beauty bottle"
                    className="bottle-hero"
                  />
                </Media>
                <Media greaterThanOrEqual="md">
                  <GatsbyImage
                    image={products}
                    alt="You Beauty bottles"
                    className="bottles-hero"
                    style={{ position: "absolute" }}
                  />
                </Media>
              </div>
            </div>
          </div>
        </div>
        <div className="orange-gradient"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 relative overflow-hidden">
          <div className="h-96 md:h-full" style={{ maxHeight: "550px" }}>
            <BgImage
              image={boxer}
              style={{ height: "100%", backgroundPosition: "20% 40%" }}
            />
          </div>
          <div className="p-4 pb-16 md:p-20 lg:py-36">
            <h2 className="text-40 text-center mb-4 text-orange">
              <span className="text-gray-light">Knock Out</span> Joint Pain.
            </h2>

            <p className="max-w-prose mx-auto leading-loose">
              Joint Pain Sucks™ is an advanced triple-action formula combining
              Boswellia, Turmeric and Ginger to target joint pain and
              inflammation. Enhanced with the latest AQUEOSOME® technology plus,
              black pepper to maximise bioavailability for optimal relief.
            </p>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row-reverse">
          <div className="max-w-md block mx-auto md:w-1/2">
            <GatsbyImage
              image={boswellia}
              alt="Crystalised boswellia droplets"
            />
          </div>
          <div className="p-6 md:p-10 md:w-1/2">
            <h2 className="text-30 mb-4 text-gray-light">Boswellia</h2>

            <p className="max-w-prose mx-auto leading-loose mb-4">
              Boswellia, also known as Indian frankincense, comes from the
              Boswellia serrata tree native to India. Boswellia serrata has
              gained much attention as a potent anti-inflammatory,
              anti-arthritic and analgesic agent.
            </p>
            <p className="max-w-prose mx-auto leading-loose">
              Boswellia’s anti-inflammatory action has been shown to ease pain,
              reduce swelling, and improve general wellbeing. These benefits
              come mainly from terpenes, and active constituents known as
              boswellic acids; most importantly acetyl-11-keto-beta-boswellic
              acid (AKBA).
            </p>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row">
          <div className="max-w-md block mx-auto md:w-1/2">
            <GatsbyImage
              image={turmeric}
              alt="Turmeric T5™ powder with its destinctive red colour"
            />
          </div>
          <div className="p-6 md:p-10 md:pt-4">
            <h2 className="text-30 mb-4 text-gray-light">Turmeric T5™</h2>

            <p className="max-w-prose mx-auto leading-loose mb-4">
              Not only does Turmeric T5™ stand out from the rest with its rich,
              red colour, it’s also the world’s first formulation using whole
              turmeric extract. Turmeric T5™ contains all five active components
              of the turmeric root - demethoxycurcumin (DMC),
              bisdemethoxycurcumin (BDMC), curcumin, essential oils and resins.
              All five active ingredients work synergistically to give turmeric
              its therapeutic action.
            </p>
            <p className="max-w-prose mx-auto leading-loose">
              Enchanced with Aqueosome® technology, Turmeric T5™ has greater
              bioavailability and a more sustained release into the body for
              longer lasting benefits.
            </p>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row-reverse">
          <div className="max-w-md block mx-auto md:w-1/2">
            <GatsbyImage image={ginger} alt="Fresh ginger roots" />
          </div>
          <div className="p-6 md:p-10 md:w-1/2">
            <h2 className="text-30 mb-4 text-gray-light">Ginger</h2>

            <p className="max-w-prose mx-auto leading-loose mb-4">
              Ginger is most commonly known as a unique, aromatic spice, but it
              has also long been used in traditional and modern medicines.
              Ginger has been shown to have anti-inflammatory properties. Ginger
              has a protective effect on joint inflammation, arthritis, and
              musculoskeletal disorders via its anti-inflammatory, antioxidant,
              and anti-serotonin activity.
            </p>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row md:items-center">
          <GatsbyImage
            image={pepper}
            alt="Turmeric T5™ powder with its destinctive red colour"
            className="max-w-md block mx-auto md:w-1/2"
          />

          <div className="p-6 md:p-10 md:pt-4 md:w-1/2">
            <h2 className="text-30 mb-4 text-gray-light">Black Pepper</h2>

            <p className="max-w-prose mx-auto leading-loose mb-4">
              Enhanced with black pepper to further increase absorbability.
            </p>
          </div>
        </div>
        <div className="bg-gray-dark p-6">
          <h2 className="text-35 md:text-40 text-white text-center">
            Results That Speak For Themselves.
          </h2>
          <div className="flex flex-col md:flex-row md:justify-center items-center">
            <CustomerQuote name="Marcus" color="orange" isRight={true}>
              <p>
                <strong>Amazing Amazing Amazing</strong>
                <br />
                Take this stuff if you want your joint pains to go away quickly.
                I have only been taking this stuff for a month and it is working
                a charm. My joint pains have never felt so good.
              </p>
            </CustomerQuote>
            <CustomerQuote name="Joseph" color="orange" isRight={false}>
              <p>
                <strong>Love "Joint Pain Sucks"</strong>
                <br />
                Absolutely swear by this product. Just last week I had a very
                sore ankle and was limping and I took JPS in the morning and by
                the afternoon my pain was gone. Completely gone.
              </p>
            </CustomerQuote>
          </div>
        </div>
      </div>
    </>
  );
};

export default JointPainSucks;

export const query = graphql`
  query {
    product: file(
      relativePath: { eq: "jointPainSucks/joint-pain-sucks-bottle.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    products: file(
      relativePath: { eq: "jointPainSucks/joint-pain-sucks-bottles.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    boxer: file(relativePath: { eq: "jointPainSucks/knock-out-pain.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    boswellia: file(relativePath: { eq: "jointPainSucks/boswellia.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    turmeric: file(relativePath: { eq: "jointPainSucks/turmeric.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    ginger: file(relativePath: { eq: "jointPainSucks/ginger.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    pepper: file(relativePath: { eq: "jointPainSucks/pepper.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;
